<script setup lang="ts">
import { computed, watch } from "vue"
import { Info, Plus, Trash2 } from "lucide-vue-next"
import { toast } from "vue-sonner"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "~/components/ui/dialog"
import { ScrollArea } from "~/components/ui/scroll-area"
import { Switch } from "~/components/ui/switch"
import { Button } from "~/components/ui/button"
import { Label } from "~/components/ui/label"
import { Alert, AlertDescription } from "~/components/ui/alert"
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "~/components/ui/select"
import { usePlatform } from "~/composables/usePlatform"
import { useNotificationOverrides, type NotificationTargetInfo } from "~/composables/useNotificationOverrides"
import NotificationSettingsMobileDrawer from "~/components/voice-ui/Notifications/NotificationSettingsMobileDrawer.vue"
import { useNotificationSettingsDialog } from "~/composables/useNotificationSettingsDialog"
import { useNotificationSettingsStore } from "~/stores/notificationSettings"
import { useGroupsStore } from "~/stores/groups"
import {
  formatMutedUntil,
  inheritedLevelLabel,
  LEVEL_LABELS,
  MUTE_OPTIONS,
  NotificationLevel,
  NotificationTargetType,
  type MuteDurationValue,
  type NotificationLevelValue,
} from "~/lib/notifications/notificationPolicy"

// «Настройки уведомлений» of one group: its mute and level, the @everyone/@here and role-mention
// suppressions, and every category/channel with a setting of its own. The desktop Dialog; mobile gets
// its own list → page Drawer (NotificationSettingsMobileDrawer.vue). Mounted once in app.vue and
// opened through useNotificationSettingsDialog.
const { isMobile } = usePlatform()
const { isOpen, groupId } = useNotificationSettingsDialog()
const store = useNotificationSettingsStore()
const groupsStore = useGroupsStore()

const gid = computed(() => groupId.value ?? "")
const groupName = computed(() =>
  groupsStore.groupDetails[gid.value]?.name ?? groupsStore.groups.find((g) => g.id === gid.value)?.name ?? "")
const settings = computed(() => store.settingsFor(gid.value))
const group = computed(() => store.resolveGroup(gid.value))

// Before the `immediate` watch below, which calls `resetAdded`.
const { overrideRows, addableCategories, addTarget: addTargetId, targetIcon, removeRow, resetAdded } = useNotificationOverrides(gid)

watch([isOpen, gid], ([open, id]) => {
  if (open && id) {
    void store.ensureLoaded(id)
    // Category/channel names come from the group's details.
    void groupsStore.loadGroupDetails(id, "notificationSettingsDialog")
  }
  if (!open) resetAdded()
}, { immediate: true })

const LEVELS = [NotificationLevel.AllMessages, NotificationLevel.OnlyMentions, NotificationLevel.Nothing] as const

async function run(action: () => Promise<unknown>) {
  try {
    await action()
  } catch {
    toast.error("Не удалось изменить настройки уведомлений")
  }
}

// ── Group ─────────────────────────────────────────────────────────────────────

function muteGroup(value: unknown) {
  void run(() => store.mute(gid.value, NotificationTargetType.Group, gid.value, Number(value) as MuteDurationValue))
}

function unmuteGroup() {
  void run(() => store.unmute(gid.value, NotificationTargetType.Group, gid.value))
}

function setGroupLevel(value: unknown) {
  const level = Number(value) as NotificationLevelValue
  if (level === group.value.level && group.value.levelSource === "group") return
  void run(() => store.setLevel(gid.value, NotificationTargetType.Group, gid.value, level))
}

function setSuppress(key: "suppress_everyone" | "suppress_roles", value: boolean) {
  void run(() => store.setSuppress(gid.value, { [key]: value }))
}

// ── Overrides ─────────────────────────────────────────────────────────────────


function addTarget(value: unknown) {
  addTargetId(String(value ?? ""))
}

function setRowLevel(info: NotificationTargetInfo, value: unknown) {
  void run(() => store.setLevel(gid.value, info.type, info.id, Number(value) as NotificationLevelValue))
}

function muteRow(info: NotificationTargetInfo, value: unknown) {
  void run(() => store.mute(gid.value, info.type, info.id, Number(value) as MuteDurationValue))
}

function unmuteRow(info: NotificationTargetInfo) {
  void run(() => store.unmute(gid.value, info.type, info.id))
}
</script>

<template>
  <NotificationSettingsMobileDrawer v-if="isMobile"/>
  <Dialog v-else v-model:open="isOpen">
    <DialogContent class="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-lg">
      <DialogHeader class="shrink-0 px-6 pt-6 pb-3">
        <DialogTitle>Настройки уведомлений</DialogTitle>
        <DialogDescription>{{ groupName }}</DialogDescription>
      </DialogHeader>

      <ScrollArea class="min-h-0 flex-1">
        <div class="space-y-6 px-6 pb-6">
          <Alert v-if="settings?.large_group">
            <Info class="size-4"/>
            <AlertDescription>В группе больше 1000 участников — Push приходят только на упоминания</AlertDescription>
          </Alert>

          <!-- Mute -->
          <section class="space-y-2">
            <Label class="text-xs font-semibold uppercase text-muted-foreground">Заглушить группу</Label>
            <div v-if="group.ownMuted" class="flex items-center justify-between gap-3 rounded-xl border bg-muted/40 px-4 py-3">
              <span class="text-sm">{{ formatMutedUntil(group.ownMutedUntil, store.now) }}</span>
              <Button size="sm" variant="secondary" @click="unmuteGroup">Включить звук</Button>
            </div>
            <Select v-else :model-value="''" @update:model-value="muteGroup">
              <SelectTrigger class="w-full rounded-xl">
                <SelectValue placeholder="Не заглушено"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="option in MUTE_OPTIONS" :key="option.value" :value="String(option.value)">
                  {{ option.label }}
                </SelectItem>
              </SelectContent>
            </Select>
            <p class="text-xs text-muted-foreground">Заглушённая группа не присылает Push и не отмечает каналы непрочитанными.</p>
          </section>

          <!-- Level -->
          <section class="space-y-2">
            <Label class="text-xs font-semibold uppercase text-muted-foreground">Уведомления группы</Label>
            <div class="divide-y divide-border/60 overflow-hidden rounded-xl border bg-muted/40">
              <button
                  v-for="level in LEVELS"
                  :key="level"
                  type="button"
                  class="flex min-h-10 w-full items-center gap-3 px-4 text-left text-sm transition-colors hover:bg-muted active:bg-muted"
                  @click="setGroupLevel(level)"
              >
                <span
                    class="flex size-4 shrink-0 items-center justify-center rounded-full border"
                    :class="group.level === level ? 'border-primary' : 'border-muted-foreground/50'"
                >
                  <span v-if="group.level === level" class="size-2 rounded-full bg-primary"/>
                </span>
                <span class="min-w-0 flex-1 truncate">{{ LEVEL_LABELS[level] }}</span>
                <span v-if="group.level === level && group.levelSource === 'default'" class="shrink-0 text-xs text-muted-foreground">
                  по умолчанию группы
                </span>
              </button>
            </div>
          </section>

          <!-- Suppressions -->
          <section class="space-y-1">
            <div class="flex items-center justify-between gap-4 py-2">
              <div class="min-w-0">
                <div class="text-sm font-medium">Подавлять @everyone и @here</div>
                <div class="text-xs text-muted-foreground">Такие упоминания не будут считаться упоминанием вас</div>
              </div>
              <Switch
                  :model-value="!!settings?.suppress_everyone"
                  @update:model-value="setSuppress('suppress_everyone', !!$event)"
              />
            </div>
            <div class="flex items-center justify-between gap-4 py-2">
              <div class="min-w-0">
                <div class="text-sm font-medium">Подавлять упоминания ролей</div>
                <div class="text-xs text-muted-foreground">Упоминание вашей роли не будет считаться упоминанием вас</div>
              </div>
              <Switch
                  :model-value="!!settings?.suppress_roles"
                  @update:model-value="setSuppress('suppress_roles', !!$event)"
              />
            </div>
          </section>

          <!-- Overrides -->
          <section class="space-y-2">
            <Label class="text-xs font-semibold uppercase text-muted-foreground">Каналы и категории</Label>
            <div v-if="overrideRows.length" class="divide-y divide-border/60 overflow-hidden rounded-xl border bg-muted/40">
              <div v-for="row in overrideRows" :key="row.info.id" class="space-y-2 px-3 py-3">
                <div class="flex items-center gap-2">
                  <component :is="targetIcon(row.info)" class="size-4 shrink-0 text-muted-foreground"/>
                  <span class="min-w-0 flex-1 truncate text-sm font-medium">{{ row.info.name }}</span>
                  <Button
                      variant="ghost"
                      size="icon"
                      class="size-8 shrink-0 text-muted-foreground"
                      aria-label="Сбросить настройки"
                      @click="removeRow(row.info, row.resolved)"
                  >
                    <Trash2 class="size-4"/>
                  </Button>
                </div>
                <div class="grid grid-cols-2 gap-2">
                  <Select :model-value="String(row.resolved.ownLevel)" @update:model-value="setRowLevel(row.info, $event)">
                    <SelectTrigger class="w-full" size="sm">
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem :value="String(NotificationLevel.Inherit)">{{ inheritedLevelLabel(row.resolved) }}</SelectItem>
                      <SelectItem v-for="level in LEVELS" :key="level" :value="String(level)">{{ LEVEL_LABELS[level] }}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                      v-if="row.resolved.ownMuted"
                      variant="secondary"
                      size="sm"
                      class="justify-start truncate"
                      @click="unmuteRow(row.info)"
                  >
                    Включить звук · {{ formatMutedUntil(row.resolved.ownMutedUntil, store.now).replace('Заглушено ', '') }}
                  </Button>
                  <Select v-else :model-value="''" @update:model-value="muteRow(row.info, $event)">
                    <SelectTrigger class="w-full" size="sm">
                      <SelectValue placeholder="Не заглушено"/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem v-for="option in MUTE_OPTIONS" :key="option.value" :value="String(option.value)">
                        Заглушить {{ option.label.toLowerCase() }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <p v-else class="text-xs text-muted-foreground">Все каналы и категории следуют настройкам группы.</p>

            <Select v-if="addableCategories.length" :model-value="''" @update:model-value="addTarget">
              <SelectTrigger class="w-full rounded-xl">
                <span class="flex items-center gap-2 text-muted-foreground">
                  <Plus class="size-4"/>
                  Добавить канал или категорию
                </span>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup v-for="entry in addableCategories" :key="entry.category.id">
                  <SelectLabel>{{ entry.category.name }}</SelectLabel>
                  <SelectItem v-if="!entry.categoryTaken" :value="entry.category.id">Вся категория</SelectItem>
                  <SelectItem v-for="channel in entry.channels" :key="channel.id" :value="channel.id"># {{ channel.name }}</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </section>
        </div>
      </ScrollArea>
    </DialogContent>
  </Dialog>
</template>
